import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COIN_ICONS = {
  bitcoin: { icon: FaBitcoin, color: '#F7931A' },
  ethereum: { icon: FaEthereum, color: '#627EEA' },
  tether: { icon: SiTether, color: '#26A17B' },
};

export default function LiveRatesTable({ compact = false }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [source, setSource] = useState('');

  const fetchRates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/rates`);
      setRates(response.data.rates);
      setLastUpdated(response.data.last_updated);
      setSource(response.data.source);
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    // Refresh rates every 60 seconds
    const interval = setInterval(fetchRates, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (!price) return '---';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-NG', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading && rates.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-primary-main" size={32} />
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="live-rates-table">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading text-xl font-semibold text-text-primary">
            {compact ? 'Live Rates' : 'Current Exchange Rates'}
          </h3>
          {lastUpdated && (
            <p className="text-text-muted text-sm">
              Last updated: {formatTime(lastUpdated)} • Source: {source}
            </p>
          )}
        </div>
        <motion.button
          onClick={fetchRates}
          whileHover={{ rotate: 180 }}
          transition={{ duration: 0.3 }}
          className="p-2 rounded-full bg-background hover:bg-border transition-colors"
          data-testid="refresh-rates-btn"
        >
          <RefreshCw size={18} className={`text-primary-main ${loading ? 'animate-spin' : ''}`} />
        </motion.button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="rates-table">
          <thead>
            <tr>
              <th>Coin</th>
              <th>Buy Rate</th>
              <th>Sell Rate</th>
              {!compact && <th>24h Change</th>}
            </tr>
          </thead>
          <tbody>
            {rates.map((rate, index) => {
              const CoinIcon = COIN_ICONS[rate.coin_id]?.icon || FaBitcoin;
              const coinColor = COIN_ICONS[rate.coin_id]?.color || '#F7931A';
              const isPositive = rate.change_24h >= 0;

              return (
                <motion.tr
                  key={rate.coin_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`rate-row-${rate.symbol}`}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      <CoinIcon size={28} style={{ color: coinColor }} />
                      <div>
                        <p className="font-semibold text-text-primary">{rate.symbol}</p>
                        <p className="text-text-muted text-sm">{rate.name}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="font-mono font-semibold text-accent-success">
                      {formatPrice(rate.buy_rate)}
                    </span>
                  </td>
                  <td>
                    <span className="font-mono font-semibold text-accent-danger">
                      {formatPrice(rate.sell_rate)}
                    </span>
                  </td>
                  {!compact && (
                    <td>
                      <div className={`flex items-center gap-1 ${isPositive ? 'text-accent-success' : 'text-accent-danger'}`}>
                        {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        <span className="font-mono">
                          {isPositive ? '+' : ''}{rate.change_24h?.toFixed(2) || 0}%
                        </span>
                      </div>
                    </td>
                  )}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
